import { lazy, Suspense } from "react";
import { Navigate, type RouteObject } from "react-router-dom";
import { Loading } from "@/shared/components/Loading";
import { AuthLayout } from "@/shared/components/layouts/AuthLayout";
import { MainLayout } from "@/shared/components/layouts/MainLayout";
import { MbosGuard } from "./MbosGuard";
import { ProtectedRoute } from "./ProtectedRoute";

// Lazy load pages
const LoginPage = lazy(() => import("@/modules/auth/pages/LoginPage"));
const DeleteAccountPage = lazy(() => import("@/modules/auth/pages/DeleteAccountPage"));
const DashboardPage = lazy(() => import("@/modules/dashboard/pages/DashboardPage"));
const OrganizationTypePositionsPage = lazy(
	() => import("@/modules/organization-type-positions/pages/OrganizationTypePositionsPage")
);
const OrganizationTypesPage = lazy(
	() => import("@/modules/organization-types/pages/OrganizationTypesPage")
);
const OrganizationDetailPage = lazy(
	() => import("@/modules/organizations/pages/OrganizationDetailPage")
);
const MyOrganizationPage = lazy(() => import("@/modules/organizations/pages/MyOrganizationPage"));
const OtherOrganizationsPage = lazy(
	() => import("@/modules/organizations/pages/OtherOrganizationsPage")
);
const StaffPage = lazy(() => import("@/modules/staff/pages/StaffPage"));
const UsersPage = lazy(() => import("@/modules/users/pages/UsersPage"));
const EmployeesAttendancePage = lazy(
	() => import("@/modules/attendance/pages/EmployeesAttendancePage")
);
const LessonAttendancePage = lazy(() => import("@/modules/attendance/pages/LessonAttendancePage"));
const ExcusesPage = lazy(() => import("@/modules/attendance/pages/ExcusesPage"));
const FaceIdDevicesPage = lazy(() => import("@/modules/attendance/pages/FaceIdDevicesPage"));
const FaceEnrollmentPage = lazy(() => import("@/modules/face-enrollment/pages/FaceEnrollmentPage"));
const StudentsPage = lazy(() => import("@/modules/students/pages/StudentsPage"));
const StudentDetailPage = lazy(() => import("@/modules/students/pages/StudentDetailPage"));
const PendingStudentsPage = lazy(() => import("@/modules/students/pages/PendingStudentsPage"));
const UnassignedStudentsPage = lazy(
	() => import("@/modules/students/pages/UnassignedStudentsPage")
);
const GuardiansPage = lazy(() => import("@/modules/guardians/pages/GuardiansPage"));
const ClassGroupsPage = lazy(() => import("@/modules/class-groups/pages/ClassGroupsPage"));
const ClassGroupDetailPage = lazy(
	() => import("@/modules/class-groups/pages/ClassGroupDetailPage")
);
const ReportsPage = lazy(() => import("@/modules/reporting/pages/ReportsPage"));
const ShiftSettingsPage = lazy(() => import("@/modules/settings/pages/ShiftSettingsPage"));
const TariffSettingsPage = lazy(
	() => import("@/modules/billing-settings/pages/TariffSettingsPage")
);
const PaymentsPage = lazy(() => import("@/modules/billing-settings/pages/PaymentsPage"));
const AcademicYearSettingsPage = lazy(
	() => import("@/modules/settings/pages/AcademicYearSettingsPage")
);
const WeekdaySettingsPage = lazy(() => import("@/modules/settings/pages/WeekdaySettingsPage"));
const SubjectSettingsPage = lazy(() => import("@/modules/settings/pages/SubjectSettingsPage"));
const RoomTypeSettingsPage = lazy(() => import("@/modules/settings/pages/RoomTypeSettingsPage"));
const AcademicTermSettingsPage = lazy(
	() => import("@/modules/settings/pages/AcademicTermSettingsPage")
);
const HolidaySettingsPage = lazy(() => import("@/modules/settings/pages/HolidaySettingsPage"));
const OrgShiftsPage = lazy(() => import("@/modules/organizations/pages/OrgShiftsPage"));
const OrgAcademicYearsPage = lazy(
	() => import("@/modules/organizations/pages/OrgAcademicYearsPage")
);
const OrgClassroomsPage = lazy(() => import("@/modules/organizations/pages/OrgClassroomsPage"));
const OrgTeachingAssignmentsPage = lazy(
	() => import("@/modules/organizations/pages/OrgTeachingAssignmentsPage")
);
const OrgSchedulePage = lazy(() => import("@/modules/organizations/pages/OrgSchedulePage"));
const OrgWorkersPage = lazy(() => import("@/modules/organizations/pages/OrgWorkersPage"));
const OrgTeachersPage = lazy(() => import("@/modules/organizations/pages/OrgTeachersPage"));
const OrgAcademicTermsPage = lazy(
	() => import("@/modules/organizations/pages/OrgAcademicTermsPage")
);
const OrgHolidaysPage = lazy(() => import("@/modules/organizations/pages/OrgHolidaysPage"));
const OrgChildrenPage = lazy(() => import("@/modules/organizations/pages/OrgChildrenPage"));
const DistrictSchoolsPage = lazy(() => import("@/modules/organizations/pages/DistrictSchoolsPage"));
const OrgUsersPage = lazy(() => import("@/modules/organizations/pages/OrgUsersPage"));
const OrgDevicesPage = lazy(() => import("@/modules/organizations/pages/OrgDevicesPage"));
const OrgFaceEnrollmentPage = lazy(
	() => import("@/modules/organizations/pages/OrgFaceEnrollmentPage")
);
const OrgStaffPage = lazy(() => import("@/modules/organizations/pages/OrgStaffPage"));
const NvrCamerasPage = lazy(() => import("@/modules/nvr-cameras/pages/NvrCamerasPage"));
const StreamGridPage = lazy(() => import("@/modules/nvr-cameras/pages/StreamGridPage"));
const StreamSinglePage = lazy(() => import("@/modules/nvr-cameras/pages/StreamSinglePage"));
const SchoolCamerasPage = lazy(() => import("@/modules/nvr-cameras/pages/SchoolCamerasPage"));
const TrashBinPage = lazy(() => import("@/modules/trash/pages/TrashBinPage"));
const ChronicAbsenteesPage = lazy(() => import("@/modules/reporting/pages/ChronicAbsenteesPage"));
const AbsentStudentsPage = lazy(() => import("@/modules/attendance/pages/AbsentStudentsPage"));
const ExcusedStudentsPage = lazy(() => import("@/modules/attendance/pages/ExcusedStudentsPage"));
const SchoolSetupPage = lazy(() => import("@/modules/school-setup/pages/SchoolSetupPage"));

// Helper component for lazy pages with Suspense
function LazyPage({ children }: { children: React.ReactNode }) {
	return <Suspense fallback={<Loading />}>{children}</Suspense>;
}

export const routes: RouteObject[] = [
	// Public routes - Auth
	{
		path: "/login",
		element: <AuthLayout />,
		children: [
			{
				index: true,
				element: (
					<LazyPage>
						<LoginPage />
					</LazyPage>
				),
			},
		],
	},
	{
		path: "/delete_account",
		element: (
			<LazyPage>
				<DeleteAccountPage />
			</LazyPage>
		),
	},

	// Protected routes
	{
		path: "/",
		element: (
			<ProtectedRoute>
				<MainLayout />
			</ProtectedRoute>
		),
		children: [
			{
				index: true,
				element: <Navigate to="/dashboard" replace />,
			},
			{
				path: "dashboard",
				element: (
					<LazyPage>
						<DashboardPage />
					</LazyPage>
				),
			},
			{
				path: "users",
				element: (
					<MbosGuard>
						<LazyPage>
							<UsersPage />
						</LazyPage>
					</MbosGuard>
				),
			},
			{
				path: "organization-types",
				element: (
					<MbosGuard>
						<LazyPage>
							<OrganizationTypesPage />
						</LazyPage>
					</MbosGuard>
				),
			},
			{
				path: "positions",
				element: (
					<MbosGuard>
						<LazyPage>
							<OrganizationTypePositionsPage />
						</LazyPage>
					</MbosGuard>
				),
			},
			{
				path: "organizations",
				element: (
					<LazyPage>
						<MyOrganizationPage />
					</LazyPage>
				),
			},
			{
				path: "organizations/children",
				element: (
					<LazyPage>
						<OrgChildrenPage />
					</LazyPage>
				),
			},
			{
				path: "organizations/schools",
				element: (
					<LazyPage>
						<DistrictSchoolsPage />
					</LazyPage>
				),
			},
			{
				path: "organizations/users",
				element: (
					<LazyPage>
						<OrgUsersPage />
					</LazyPage>
				),
			},
			{
				path: "organizations/devices",
				element: (
					<LazyPage>
						<OrgDevicesPage />
					</LazyPage>
				),
			},
			{
				path: "organizations/face-enrollment",
				element: (
					<LazyPage>
						<OrgFaceEnrollmentPage />
					</LazyPage>
				),
			},
			{
				path: "organizations/staff",
				element: (
					<LazyPage>
						<OrgStaffPage />
					</LazyPage>
				),
			},
			{
				path: "organizations/:id",
				element: (
					<LazyPage>
						<OrganizationDetailPage />
					</LazyPage>
				),
			},
			{
				path: "cross-access",
				element: (
					<LazyPage>
						<OtherOrganizationsPage />
					</LazyPage>
				),
			},
			// Attendance routes
			{
				path: "attendance/employees",
				element: (
					<LazyPage>
						<EmployeesAttendancePage />
					</LazyPage>
				),
			},
			{
				path: "attendance/students",
				element: (
					<LazyPage>
						<LessonAttendancePage />
					</LazyPage>
				),
			},
			{
				path: "attendance/faceid-devices",
				element: (
					<LazyPage>
						<FaceIdDevicesPage />
					</LazyPage>
				),
			},
			{
				path: "attendance/nvr-cameras",
				element: (
					<LazyPage>
						<NvrCamerasPage />
					</LazyPage>
				),
			},
			{
				path: "attendance/nvr-cameras/stream/:nvrDeviceId",
				element: (
					<LazyPage>
						<StreamGridPage />
					</LazyPage>
				),
			},
			{
				path: "attendance/nvr-cameras/stream/:nvrDeviceId/:channelId",
				element: (
					<LazyPage>
						<StreamSinglePage />
					</LazyPage>
				),
			},
			{
				path: "attendance/school-cameras",
				element: (
					<LazyPage>
						<SchoolCamerasPage />
					</LazyPage>
				),
			},
			{
				path: "attendance/excuses",
				element: (
					<LazyPage>
						<ExcusesPage />
					</LazyPage>
				),
			},
			{
				path: "attendance/face-enrollment",
				element: (
					<LazyPage>
						<FaceEnrollmentPage />
					</LazyPage>
				),
			},
			{
				path: "attendance/absent-students",
				element: (
					<LazyPage>
						<AbsentStudentsPage />
					</LazyPage>
				),
			},
			{
				path: "attendance/excused-students",
				element: (
					<LazyPage>
						<ExcusedStudentsPage />
					</LazyPage>
				),
			},
			{
				path: "attendance/chronic-absentees",
				element: (
					<LazyPage>
						<ChronicAbsenteesPage />
					</LazyPage>
				),
			},
			{
				path: "students",
				element: (
					<LazyPage>
						<StudentsPage />
					</LazyPage>
				),
			},
			{
				path: "students/pending",
				element: (
					<LazyPage>
						<PendingStudentsPage />
					</LazyPage>
				),
			},
			{
				path: "students/unassigned",
				element: (
					<LazyPage>
						<UnassignedStudentsPage />
					</LazyPage>
				),
			},
			{
				path: "students/:id",
				element: (
					<LazyPage>
						<StudentDetailPage />
					</LazyPage>
				),
			},
			{
				path: "guardians",
				element: (
					<LazyPage>
						<GuardiansPage />
					</LazyPage>
				),
			},
			{
				path: "classes",
				element: (
					<LazyPage>
						<ClassGroupsPage />
					</LazyPage>
				),
			},
			{
				path: "classes/:id",
				element: (
					<LazyPage>
						<ClassGroupDetailPage />
					</LazyPage>
				),
			},
			{
				path: "school-setup",
				element: (
					<LazyPage>
						<SchoolSetupPage />
					</LazyPage>
				),
			},
			{
				// Faol qadam URL da — brauzerning "orqaga" tugmasi va havola ulashish uchun
				path: "school-setup/:stepKey",
				element: (
					<LazyPage>
						<SchoolSetupPage />
					</LazyPage>
				),
			},
			{
				path: "settings/shifts",
				element: (
					<MbosGuard>
						<LazyPage>
							<ShiftSettingsPage />
						</LazyPage>
					</MbosGuard>
				),
			},
			{
				path: "settings/academic-years",
				element: (
					<MbosGuard>
						<LazyPage>
							<AcademicYearSettingsPage />
						</LazyPage>
					</MbosGuard>
				),
			},
			{
				path: "settings/weekdays",
				element: (
					<MbosGuard>
						<LazyPage>
							<WeekdaySettingsPage />
						</LazyPage>
					</MbosGuard>
				),
			},
			{
				path: "settings/subjects",
				element: (
					<LazyPage>
						<SubjectSettingsPage />
					</LazyPage>
				),
			},
			{
				path: "settings/room-types",
				element: (
					<MbosGuard>
						<LazyPage>
							<RoomTypeSettingsPage />
						</LazyPage>
					</MbosGuard>
				),
			},
			{
				path: "settings/tariffs",
				element: (
					<MbosGuard>
						<LazyPage>
							<TariffSettingsPage />
						</LazyPage>
					</MbosGuard>
				),
			},
			{
				path: "settings/payments",
				element: (
					<MbosGuard>
						<LazyPage>
							<PaymentsPage />
						</LazyPage>
					</MbosGuard>
				),
			},
			{
				path: "settings/academic-terms",
				element: (
					<MbosGuard>
						<LazyPage>
							<AcademicTermSettingsPage />
						</LazyPage>
					</MbosGuard>
				),
			},
			{
				path: "settings/holidays",
				element: (
					<MbosGuard>
						<LazyPage>
							<HolidaySettingsPage />
						</LazyPage>
					</MbosGuard>
				),
			},
			{
				path: "settings/staff",
				element: (
					<MbosGuard>
						<LazyPage>
							<StaffPage />
						</LazyPage>
					</MbosGuard>
				),
			},
			{
				path: "trash",
				element: (
					<MbosGuard>
						<LazyPage>
							<TrashBinPage />
						</LazyPage>
					</MbosGuard>
				),
			},
			{
				path: "reports",
				element: (
					<LazyPage>
						<ReportsPage />
					</LazyPage>
				),
			},
			// Educational org routes
			{
				path: "educational/shifts",
				element: (
					<LazyPage>
						<OrgShiftsPage />
					</LazyPage>
				),
			},
			{
				path: "educational/academic-years",
				element: (
					<LazyPage>
						<OrgAcademicYearsPage />
					</LazyPage>
				),
			},
			{
				path: "educational/classrooms",
				element: (
					<LazyPage>
						<OrgClassroomsPage />
					</LazyPage>
				),
			},
			{
				path: "educational/teaching-assignments",
				element: (
					<LazyPage>
						<OrgTeachingAssignmentsPage />
					</LazyPage>
				),
			},
			{
				path: "educational/schedule",
				element: (
					<LazyPage>
						<OrgSchedulePage />
					</LazyPage>
				),
			},
			{
				path: "educational/workers",
				element: (
					<LazyPage>
						<OrgWorkersPage />
					</LazyPage>
				),
			},
			{
				path: "educational/teachers",
				element: (
					<LazyPage>
						<OrgTeachersPage />
					</LazyPage>
				),
			},
			{
				path: "educational/academic-terms",
				element: (
					<LazyPage>
						<OrgAcademicTermsPage />
					</LazyPage>
				),
			},
			{
				path: "educational/holidays",
				element: (
					<LazyPage>
						<OrgHolidaysPage />
					</LazyPage>
				),
			},
		],
	},

	// 404 - Redirect to dashboard or login
	{
		path: "*",
		element: <Navigate to="/" replace />,
	},
];
